/**
 * Guesses a transcript `speaker_label` → `face_label`s map for a story with
 * exactly two speakers and no Face Mapping, so active-speaker framing can
 * still alternate between the two people as the speaker changes.
 *
 * A wrong guess is all-or-nothing — every scene frames the listener — so one
 * correction in the reframe modal fixes the whole video (PLAT-11992).
 *
 * Only speaker A may come back with labels: speaker B is then resolved per
 * scene as "the person who isn't A" (see `selectClusterExcludingLabels`),
 * which copes with B being tracked under a different label in every shot.
 */

import { err, ok, type Result } from '@src/services/common/Result';
import { LogManager } from '@src/utility/Logger';

import {
  extractSpeakerIntervals,
  type TranscriptWord,
} from './speakerIntervalUtils';

import type { FaceDetection } from './types';

const logger = LogManager.getLogger('framing:guessTwoSpeakerFaceMap');

/** A face label holding less than this share of all detections is not a person to frame (background, passers-by). */
export const MIN_PRIMARY_SHARE = 0.05;

/**
 * A label belongs to a speaker when it is on screen at least this many times
 * more often (per second of talk) during their turns than during the other
 * speaker's. Multi-camera close-ups separate cleanly; a wide two-shot, where
 * both faces are always on screen, does not.
 */
export const SEPARATION_RATIO = 1.5;

export function guessTwoSpeakerFaceMap(
  transcript: ReadonlyArray<TranscriptWord>,
  detections: ReadonlyArray<Pick<FaceDetection, 'time' | 'label'>>,
): Result<Record<string, string[]>> {
  const intervals = extractSpeakerIntervals(transcript).filter(
    interval => interval.speakerLabel !== 'unknown',
  );

  const talkTime = new Map<string, number>();
  for (const { speakerLabel, startTime, endTime } of intervals) {
    talkTime.set(
      speakerLabel,
      (talkTime.get(speakerLabel) ?? 0) + (endTime - startTime),
    );
  }
  if (talkTime.size !== 2) {
    return err('PRECONDITION', 'Needs exactly two transcript speakers', {
      speakerCount: talkTime.size,
    });
  }

  const labelCounts = new Map<string, number>();
  for (const { label } of detections) {
    labelCounts.set(label, (labelCounts.get(label) ?? 0) + 1);
  }
  const primaryLabels = [...labelCounts.entries()]
    .filter(([, count]) => count / detections.length >= MIN_PRIMARY_SHARE)
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([label]) => label);
  if (primaryLabels.length === 0) {
    return err('NOT_FOUND', 'No face is on screen often enough to frame');
  }

  const [speakerA, speakerB] = [...talkTime.keys()];
  const coOccurrence = countCoOccurrence(intervals, detections);

  const map: Record<string, string[]> = {};
  for (const label of primaryLabels) {
    const rateA =
      (coOccurrence.get(label)?.get(speakerA) ?? 0) / talkTime.get(speakerA)!;
    const rateB =
      (coOccurrence.get(label)?.get(speakerB) ?? 0) / talkTime.get(speakerB)!;
    if (rateA >= rateB * SEPARATION_RATIO && rateA > 0) {
      (map[speakerA] ??= []).push(label);
    } else if (rateB >= rateA * SEPARATION_RATIO && rateB > 0) {
      (map[speakerB] ??= []).push(label);
    }
  }

  if (Object.keys(map).length > 0) {
    logger.info('Guessed speaker→face map from on-screen timing', { map });
    return ok(map);
  }

  // Nothing separates them (a two-shot): the speaker who talks most is
  // usually the subject, and the face seen most is usually theirs.
  const dominantSpeaker =
    talkTime.get(speakerA)! >= talkTime.get(speakerB)! ? speakerA : speakerB;
  const fallback = { [dominantSpeaker]: [primaryLabels[0]] };
  logger.info('Guessed speaker→face map from talk time and face frequency', {
    map: fallback,
  });
  return ok(fallback);
}

/** face_label → speaker_label → detections while that speaker is talking. */
function countCoOccurrence(
  intervals: ReadonlyArray<{
    speakerLabel: string;
    startTime: number;
    endTime: number;
  }>,
  detections: ReadonlyArray<Pick<FaceDetection, 'time' | 'label'>>,
): Map<string, Map<string, number>> {
  const sortedIntervals = [...intervals].sort(
    (a, b) => a.startTime - b.startTime,
  );
  const sortedDetections = [...detections].sort((a, b) => a.time - b.time);

  const counts = new Map<string, Map<string, number>>();
  let i = 0;
  for (const detection of sortedDetections) {
    while (
      i < sortedIntervals.length &&
      sortedIntervals[i].endTime < detection.time
    ) {
      i++;
    }
    const interval = sortedIntervals[i];
    if (!interval || interval.startTime > detection.time) continue;

    let perSpeaker = counts.get(detection.label);
    if (!perSpeaker) {
      perSpeaker = new Map();
      counts.set(detection.label, perSpeaker);
    }
    perSpeaker.set(
      interval.speakerLabel,
      (perSpeaker.get(interval.speakerLabel) ?? 0) + 1,
    );
  }
  return counts;
}
